import {
    API,
    SortDirection,
    graphqlOperation
} from "aws-amplify";
import {
    CreateIssueMutation,
    GetIssueQuery,
    GetIssueQueryVariables,
    Issue,
    ListIssuesByLocationQuery,
    ListIssuesByLocationQueryVariables,
    ListIssuesByPropertyQuery,
    ListIssuesByPropertyQueryVariables,
    ProposalDocLink,
    UpdateIssueMutation,
    UpdateIssueMutationVariables,
} from "../../../../API";
import {
    getIssue,
    listIssuesByLocation,
    listIssuesByProperty
} from "../../../../graphql/queries";

import ClientLogger from "../../../logging/ClientLogger";
import CreateIssueError from "../error/CreateIssueError";
import GetIssueByIdError from "../error/GetIssueByIdError";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import { IDTokenSupplier } from "../../../auth/IDTokenSupplier";
import { IssueDAO } from "../IssueDAO";
import IssueRecordAlreadyExistsError from "../error/IssueRecordAlreadyExistsError";
import ListIssuesByPropertyIdError from "../error/ListIssuesByPropertyIdError";
import { createIssue, updateIssue } from "../../../../graphql/mutations";
import _ from "lodash";
import { ListResponse } from "../../../util/data/list/ListResponse";

export class GraphQLIssueDAO implements IssueDAO {

    private static readonly CREATE_ATTEMPT_METRIC_NAME = "GraphQLIssueDAO.Create.Attempt";
    private static readonly CREATE_FAILURE_METRIC_NAME = "GraphQLIssueDAO.Create.Failure";
    private static readonly GET_BY_ID_ATTEMPT_METRIC_NAME = "GraphQLIssueDAO.GetById.Attempt";
    private static readonly GET_BY_ID_FAILURE_METRIC_NAME = "GraphQLIssueDAO.GetById.Failure";
    private static readonly EDIT_ISSUE_ATTEMPT_METRIC_NAME = "GraphQLIssueDAO.Edit.Attempt";
    private static readonly EDIT_ISSUE_FAILURE_METRIC_NAME = "GraphQLIssueDAO.Edit.Failure";
    private static readonly GET_BY_LOCATION_ID_ATTEMPT_METRIC_NAME = "GraphQLIssueDAO.GetByLocationId.Attempt";
    private static readonly GET_BY_LOCATION_ID_FAILURE_METRIC_NAME = "GraphQLIssueDAO.GetByLocationId.Failure";
    private static readonly GET_BY_PROPERTY_ID_ATTEMPT_METRIC_NAME = "GraphQLIssueDAO.GetByPropertyId.Attempt";
    private static readonly GET_BY_PROPERTY_ID_FAILURE_METRIC_NAME = "GraphQLIssueDAO.GetByPropertyId.Failure";

    private api: typeof API;
    private gqlOperation: typeof graphqlOperation;
    private logger: ClientLogger;
    private readonly idTokenSupplier: IDTokenSupplier;

    constructor(
        api: typeof API,
        gqlOperation: typeof graphqlOperation,
        logger: ClientLogger,
        idTokenSupplier: IDTokenSupplier
    ) {
        this.api = api;
        this.gqlOperation = gqlOperation;
        this.logger = logger;
        this.idTokenSupplier = idTokenSupplier;
    }

    public async getIssueById(id: string): Promise<Issue> {
        try {
            this.logger.info(
                `Retrieving Issue with id=${id}`,
                undefined,
                [GraphQLIssueDAO.GET_BY_ID_ATTEMPT_METRIC_NAME]
            );
            const authToken: string = await this.idTokenSupplier.get();
            const result: GraphQLResult<GetIssueQuery> = await this.api.graphql(
                this.gqlOperation(
                    getIssue,
                    { id } satisfies GetIssueQueryVariables,
                    authToken)
            ) as GraphQLResult<GetIssueQuery>;
            return result.data?.getIssue!;
        } catch (error) {
            const errorMessage = `Failed to retrieve Issue with id=${id}`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLIssueDAO.GET_BY_ID_FAILURE_METRIC_NAME]
            );
            throw new GetIssueByIdError(
                errorMessage, {
                cause: error as Error
            });
        }
    }

    public getIssuesByLocationIdWithLimitAndSortDirection(
        locationId: string,
        sortDirection: SortDirection,
        limit: number
    ): Promise<Issue[]> {
        throw new Error("Method not implemented.");
    }

    public async getIssuesByPropertyId(propertyId: string, nextToken?: string): Promise<ListResponse<Issue>> {
        try {
            this.logger.info(
                `Retrieving Issues with propertyId=${propertyId} and nextToken=${nextToken}`,
                undefined,
                [GraphQLIssueDAO.GET_BY_PROPERTY_ID_ATTEMPT_METRIC_NAME]
            );
            const authToken: string = await this.idTokenSupplier.get();
            const result: GraphQLResult<ListIssuesByPropertyQuery> = await this.api.graphql(
                this.gqlOperation(
                    listIssuesByProperty,
                    {
                        propertyId,
                        filter: {
                            _deleted: {
                                ne: true
                            }
                        },
                        nextToken
                    } satisfies ListIssuesByPropertyQueryVariables,
                    authToken
                )
            ) as GraphQLResult<ListIssuesByPropertyQuery>;
            return {
                items: result.data?.listIssuesByProperty?.items! as Issue[],
                nextToken: result.data?.listIssuesByProperty?.nextToken ?? null
            };
        } catch (error) {
            const errorMessage = `Failed to retrieve Issues with propertyId=${propertyId} and nextToken=${nextToken}`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLIssueDAO.GET_BY_PROPERTY_ID_FAILURE_METRIC_NAME]
            );
            throw new ListIssuesByPropertyIdError(
                errorMessage, {
                cause: error as Error
            });
        }
    }

    public async getIssuesByLocationId(locationId: string): Promise<Issue[]> {
        try {
            this.logger.info(
                `Retrieving Issues with locationId=${locationId}`,
                undefined,
                [GraphQLIssueDAO.GET_BY_LOCATION_ID_ATTEMPT_METRIC_NAME]
            );
            const authToken: string = await this.idTokenSupplier.get();
            const result: GraphQLResult<ListIssuesByLocationQuery> = await this.api.graphql(
                this.gqlOperation(
                    listIssuesByLocation,
                    {
                        locationId,
                        filter: {
                            _deleted: {
                                ne: true
                            }
                        }
                    } satisfies ListIssuesByLocationQueryVariables,
                    authToken)
            ) as GraphQLResult<ListIssuesByLocationQuery>;
            return result.data?.listIssuesByLocation?.items! as Issue[];
        } catch (error) {
            const errorMessage = `Failed to retrieve Issues with locationId=${locationId}`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLIssueDAO.GET_BY_LOCATION_ID_FAILURE_METRIC_NAME]
            );
            throw new ListIssuesByPropertyIdError(
                errorMessage, {
                cause: error as Error
            });
        }
    }

    public async createNewIssue(issue: Issue): Promise<Issue> {
        this.logger.info(
            `Creating Issue with id=${issue.id}`,
            issue,
            [GraphQLIssueDAO.CREATE_ATTEMPT_METRIC_NAME]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const result: GraphQLResult<CreateIssueMutation> = await this.api.graphql(
                this.gqlOperation(
                    createIssue,
                    { input: issue },
                    authToken
                )
            ) as GraphQLResult<CreateIssueMutation>;
            return result.data?.createIssue!;
        } catch (error) {
            if ((error as any)?.errors && (error as any)?.errors[0].errorType === "ConditionalCheckFailedException") {
                const errorMessage = `Issue record already exists: ${issue.id}`;
                this.logger.warn(
                    errorMessage,
                    error,
                    [GraphQLIssueDAO.CREATE_FAILURE_METRIC_NAME]
                );
                throw new IssueRecordAlreadyExistsError(
                    errorMessage
                );
            }
            const errorMessage = `Unable to create Issue with id=${issue.id}`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLIssueDAO.CREATE_FAILURE_METRIC_NAME]
            );
            throw new CreateIssueError(errorMessage, {
                cause: error as Error
            });
        }
    }

    public async editIssue(id: string, issue: Issue): Promise<Issue> {
        try {
            this.logger.info(
                `Updating Issue with id=${id}`,
                issue,
                [GraphQLIssueDAO.EDIT_ISSUE_ATTEMPT_METRIC_NAME]
            );

            const currentIssue = await this.getIssueById(id);

            const mapProposalDocLink = (link: ProposalDocLink) => ({
                link: link.link,
                name: link.name,
                date: link.date
            });

            const variables = {
                input: {
                    id: id,
                    name: issue.name ?? currentIssue.name,
                    note: issue.note ?? currentIssue.note,
                    locationId: issue.locationId ?? currentIssue.locationId,
                    propertyId: issue.propertyId ?? currentIssue.propertyId,
                    status: issue.status ?? currentIssue.status,
                    priority: issue.priority ?? currentIssue.priority,
                    tagIds: issue.tagIds ?? currentIssue.tagIds,
                    proposalDocLinks: [
                        ...(currentIssue.proposalDocLinks ?? []).map(mapProposalDocLink),
                        ...(issue.proposalDocLinks ?? []).map(mapProposalDocLink)
                    ],
                    _version: currentIssue._version
                }
            } satisfies UpdateIssueMutationVariables;

            const authToken: string = await this.idTokenSupplier.get();

            const result: GraphQLResult<UpdateIssueMutation> = await this.api.graphql(
                this.gqlOperation(
                    updateIssue,
                    variables,
                    authToken
                )
            ) as GraphQLResult<UpdateIssueMutation>;

            if (result.data?.updateIssue) {
                return result.data.updateIssue as Issue;
            } else {
                throw new Error(`Failed to update Issue with id=${id}`);
            }
        } catch (error) {
            this.logger.error(
                `Unable to update Issue with id=${id}`,
                error,
                [GraphQLIssueDAO.EDIT_ISSUE_FAILURE_METRIC_NAME]
            );
            throw new Error(`Unable to update Issue with id=${id}`);
        }
    }

    public deleteIssue(issueId: string): Promise<void> {
        throw new Error("Method not implemented.");
    }
}